import React, { useEffect, useMemo, useState } from 'react';
import { CalendarRange, ListChecks, Scale, FileSpreadsheet } from 'lucide-react';
import { api, extractError } from '../lib/api';
import { toast } from 'sonner';
import { useAuth } from '../lib/auth';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import AbsenceTimeline from '../components/absences/AbsenceTimeline';
import AbsenceList from '../components/absences/AbsenceList';
import AbsenceBalances from '../components/absences/AbsenceBalances';
import PolicyAssignments from '../components/absences/PolicyAssignments';
import {
  AbsenceFormDialog, SchoolPatternDialog, EditSchoolPatternDialog, EmployeeAbsenceDetailDialog,
} from '../components/absences/AbsenceDialogs';

export default function AbsencesPage() {
  const { can, isAdmin } = useAuth();
  const canView = (res) => (isAdmin || can(res, 'view'));

  const canAbsCreate = can('absences', 'create');
  const canAbsEdit = can('absences', 'edit');
  const canAbsDelete = can('absences', 'delete');

  const [employees, setEmployees] = useState([]);
  const [costCenters, setCostCenters] = useState([]);
  const [reloadKey, setReloadKey] = useState(0);

  // Dialoge
  const [formOpen, setFormOpen] = useState(false);
  const [formInitial, setFormInitial] = useState(null);
  const [formEdit, setFormEdit] = useState(null);
  const [patternOpen, setPatternOpen] = useState(false);
  const [patternEditOpen, setPatternEditOpen] = useState(false);
  const [patternEditData, setPatternEditData] = useState(null);
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    Promise.all([api.get('/employees'), api.get('/cost-centers')])
      .then(([e, c]) => { setEmployees(e.data); setCostCenters(c.data); })
      .catch(err => toast.error(extractError(err)));
  }, []);

  const bump = () => setReloadKey(k => k + 1);

  const openCreate = (initial) => { setFormEdit(null); setFormInitial(initial || {}); setFormOpen(true); };
  const openEdit = (range) => { setFormInitial(null); setFormEdit(range); setFormOpen(true); };
  const openPatternEdit = (data) => { setPatternEditData(data); setPatternEditOpen(true); };
  const openDetail = (employeeId, costCenterName, year) => setDetail({ employeeId, costCenterName, year: year || new Date().getFullYear() });

  const tabs = useMemo(() => {
    const t = [];
    if (canView('absences')) {
      t.push({ key: 'calendar', label: 'Kalender', icon: CalendarRange });
      t.push({ key: 'list', label: 'Liste', icon: ListChecks });
    }
    if (canView('absence_balances')) t.push({ key: 'balances', label: 'Richtlinien-Saldi', icon: Scale });
    if (canView('absence_policies')) t.push({ key: 'policies', label: 'Richtlinienzuordnungen', icon: FileSpreadsheet });
    return t;
  }, [can, isAdmin]);

  const [tab, setTab] = useState(null);
  useEffect(() => { if (!tab && tabs.length) setTab(tabs[0].key); }, [tabs, tab]);

  return (
    <div className="p-4 md:p-6 space-y-4">
      <div>
        <h1 className="text-xl font-bold font-display text-slate-900">Abwesenheit der Mitarbeitenden</h1>
        <p className="text-sm text-slate-500">Kalender, Einträge, Urlaubs-Saldi und Richtlinien – Wochenbeginn Montag, Datumsformat TT.MM.JJJJ.</p>
      </div>

      {tab && (
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            {tabs.map(t => <TabsTrigger key={t.key} value={t.key} data-testid={`abs-tab-${t.key}`}><t.icon size={14} className="mr-1.5" />{t.label}</TabsTrigger>)}
          </TabsList>

          <TabsContent value="calendar">
            <AbsenceTimeline employees={employees} costCenters={costCenters} canCreate={canAbsCreate}
              onOpenCreate={openCreate} onOpenEdit={openEdit} reloadKey={reloadKey} />
          </TabsContent>

          <TabsContent value="list">
            <AbsenceList employees={employees} canCreate={canAbsCreate} canEdit={canAbsEdit} canDelete={canAbsDelete}
              onOpenCreate={openCreate} onOpenEdit={openEdit}
              onOpenPatternCreate={() => setPatternOpen(true)} onOpenPatternEdit={openPatternEdit} reloadKey={reloadKey} />
          </TabsContent>

          <TabsContent value="balances">
            <AbsenceBalances costCenters={costCenters} canEdit={isAdmin || can('absence_balances', 'edit')}
              canDelete={isAdmin || can('absence_balances', 'delete')} onOpenDetail={openDetail} reloadKey={reloadKey} />
          </TabsContent>

          <TabsContent value="policies">
            <PolicyAssignments costCenters={costCenters} canEdit={isAdmin || can('absence_policies', 'edit')} reloadKey={reloadKey} />
          </TabsContent>
        </Tabs>
      )}

      {/* Dialoge */}
      <AbsenceFormDialog open={formOpen} onOpenChange={setFormOpen} employees={employees}
        initial={formInitial} editRange={formEdit} onSaved={bump} />
      <SchoolPatternDialog open={patternOpen} onOpenChange={setPatternOpen} employees={employees} onChanged={bump} />
      <EditSchoolPatternDialog open={patternEditOpen} onOpenChange={setPatternEditOpen} data={patternEditData} employees={employees} onChanged={bump} />
      <EmployeeAbsenceDetailDialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}
        employeeId={detail?.employeeId} year={detail?.year} costCenterName={detail?.costCenterName} />
    </div>
  );
}
